import { type MouseEvent, type MouseEventHandler } from "react";
import { IconFa, Link, Text } from "@cruk/cruk-react-components";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { faCircle } from "@fortawesome/free-regular-svg-icons";

import { useActiveSectionContext } from "@fwa/src/contexts/ActiveSectionContext";
import { useOptimizelyContext } from "@fwa/src/contexts/OptimizelyContext";

import styles from "@fwa/src/components/ProgressListButton/styles.module.css";

import type { ProgressItemType, PageActiveSectionType } from "@fwa/src/types";

export const ProgressListButton = ({
  progressItem,
  onClick: externalOnClick,
}: {
  progressItem: ProgressItemType;
  onClick?: MouseEventHandler<HTMLElement>;
}) => {
  const [, setActiveSectionContext] = useActiveSectionContext();
  const { trackEventOptimizely } = useOptimizelyContext();
  const handleLinkClick = (fieldName?: PageActiveSectionType) => {
    if (!fieldName) return;
    const element = document.getElementById(fieldName);
    // "post" is a special case as it is a textarea outside an edit-in-place modal like component
    // so we need to scroll to the parent of the textarea, giving some context like the label
    element?.scrollIntoView({
      behavior: "smooth",
      block: fieldName == "post" ? "start" : "center",
    });

    setTimeout(() => {
      setActiveSectionContext({
        activeSection: fieldName,
      });
    }, 1000);
  };

  return (
    <button
      type="button"
      className={styles["progress-button"]}
      onClick={(e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        handleLinkClick(progressItem.fieldName);
        trackEventOptimizely({
          eventKey: `of_progress_click_${progressItem.fieldName}`,
        });
        if (externalOnClick) {
          externalOnClick(e as MouseEvent<HTMLButtonElement>);
        }
      }}
      data-cta-type={progressItem.dataCtaType}
    >
      <span className={styles["row-stretch-centered"]}>
        <Text
          as="span"
          marginRight="xs"
          marginBottom="none"
          textColor="tertiary"
        >
          <span className="screen-reader-only">
            {progressItem.isDone ? "completed" : "to do"}
          </span>
          {progressItem.isDone ? (
            <IconFa faIcon={faCircleCheck} size="1.5em" />
          ) : (
            <IconFa faIcon={faCircle} size="1.5em" />
          )}
        </Text>
        <Link as="span">{progressItem.label}</Link>
      </span>
    </button>
  );
};

export default ProgressListButton;
